import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';
import { ProgramSetup } from '../../components/affiliate/ProgramSetup';
import { ProgramOwnerDashboard } from '../../components/affiliate/AffiliateDashboard';
import { AffiliateSignupForm } from '../../components/affiliate/AffiliateSignupForm';
import AffiliatesList from '../../components/affiliate/AffiliatesList';
import { AffiliateDetails } from '../../components/affiliate/AffiliateDetails';
import PayoutsManagement from '../../components/affiliate/PayoutsManagement';
import ReportsAnalytics from '../../components/affiliate/ReportsAnalytics';
import AffiliateSettings from '../../components/affiliate/AffiliateSettings';
import ProgramsManagement from '../../components/affiliate/ProgramsManagement';
import TrackingCodeGenerator from '../../components/affiliate/TrackingCodeGenerator';
import AddAffiliateModal from '../../components/affiliate/AddAffiliateModal';
import PlansManagement from '../../components/affiliate/PlansManagement';
import TiersManagement from '../../components/affiliate/TiersManagement';
import ApprovalsQueue from '../../components/affiliate/ApprovalsQueue';
import AttributionSettings from '../../components/affiliate/AttributionSettings';
import WebhooksManagement from '../../components/affiliate/WebhooksManagement';
import CommissionsManagement from '../../components/affiliate/CommissionsManagement';
import IntegrationsHub from '../../components/affiliate/IntegrationsHub';
import CreativesManagement from '../../components/affiliate/CreativesManagement';

interface AffiliateProgram {
  id: string;
  program_name: string;
  default_commission_rate: number;
  commission_type: string;
  cookie_duration_days: number;
  minimum_payout: number;
  payment_terms: string;
  created_at: string;
}

const AffiliateManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [hasProgram, setHasProgram] = useState<boolean>(true);
  const [program, setProgram] = useState<AffiliateProgram | null>(null);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [showProgramSetup, setShowProgramSetup] = useState<boolean>(false);
  
  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // User is authenticated, proceed with checking for existing programs
    checkForExistingProgram();
  }, [user, navigate]);
  
  // Check if the user already has an affiliate program
  const checkForExistingProgram = async () => {
    try {
      setLoading(true);

      const data = await affiliateService.getPrograms();
      const programs = Array.isArray((data as any)?.programs) ? (data as any).programs : [];

      if (programs.length > 0) {
        setHasProgram(true);
        setProgram(programs[0]);
      } else {
        setHasProgram(false);
        setProgram(null);
      }
    } catch (error) {
      console.error('Error checking for affiliate program:', error);
      // Only show error toast for actual errors, not for missing data
      toast.error('Failed to load affiliate program details');
      
      // Set hasProgram to false so user can create a new program
      setHasProgram(false);
      setProgram(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle affiliate selection for details view
  const handleAffiliateSelect = (affiliateId: string) => {
    setSelectedAffiliateId(affiliateId);
    setActiveTab('affiliate-details');
  };

  // Handle back button from affiliate details
  const handleBackToList = () => {
    setSelectedAffiliateId(null);
    setActiveTab('affiliates');
  };
  
  // Handle opening the add affiliate modal
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };
  
  // Handle closing the add affiliate modal
  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };
  
  // Handle affiliate saved callback
  const handleAffiliateSaved = () => {
    // Reload the affiliates list
    if (activeTab === 'affiliates') {
      // The component will refetch when it mounts
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }
    
    if (showProgramSetup) {
      return <ProgramSetup onProgramCreated={async () => { setShowProgramSetup(false); await checkForExistingProgram(); }} />;
    }
    
    // If no program yet and user navigates to a program-dependent tab, show guidance instead of rendering broken components
    const requiresProgram = (
      tab: string,
    ) => ['plans','tiers','approvals','attribution','webhooks','commissions','creatives','affiliates','affiliate-details','payouts','reports','tracking-code','signup-form'].includes(tab);
    if (!hasProgram && requiresProgram(activeTab)) {
      return (
        <div className="rounded-md border border-dashed border-gray-300 p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">You don’t have an affiliate program yet</h2>
          <p className="text-gray-600 mb-6">Create a program to enable all management features. You can still explore the dashboard overview.</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowProgramSetup(true)}
              className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Create Program
            </button>
            <button
              onClick={() => setActiveTab('overview')}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      );
    }
    
    switch (activeTab) {
      case 'overview':
        if (!program) {
          return (
            <div>
              <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4">
                <p className="text-blue-800">
                  You don’t have an affiliate program yet. You can explore the dashboard or create your program now to unlock full functionality.
                </p>
              </div>
              <div className="flex items-center gap-3 mb-8">
                <button
                  onClick={() => setShowProgramSetup(true)}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Create Program
                </button>
                <button
                  onClick={checkForExistingProgram}
                  className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Refresh
                </button>
              </div>
              {/* Lightweight empty overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-500">Total Affiliates</p>
                  <p className="text-2xl font-semibold">—</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-500">Clicks (30d)</p>
                  <p className="text-2xl font-semibold">—</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-gray-500">Conversions (30d)</p>
                  <p className="text-2xl font-semibold">—</p>
                </div>
              </div>
            </div>
          );
        }
        return <ProgramOwnerDashboard program={program} />;
      case 'programs':
        return <ProgramsManagement />;
      case 'plans':
        return <PlansManagement programId={program?.id} />;
      case 'tiers':
        return <TiersManagement programId={program?.id} />;
      case 'approvals':
        return <ApprovalsQueue programId={program?.id} />;
      case 'attribution':
        return <AttributionSettings programId={program?.id} />;
      case 'webhooks':
        return <WebhooksManagement />;
      case 'commissions':
        return <CommissionsManagement />;
      case 'creatives':
        return <CreativesManagement programId={program?.id || ''} />;
      case 'integrations':
        return <IntegrationsHub />;
      case 'affiliates':
        return (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Affiliates</h2>
              <button
                onClick={handleOpenAddModal}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Affiliate
              </button>
            </div>
            <AffiliatesList programId={program?.id} onAffiliateSelect={handleAffiliateSelect} />
            <AddAffiliateModal 
              isOpen={isAddModalOpen} 
              onClose={handleCloseAddModal} 
              programId={program?.id || ''} 
              onAffiliateSaved={handleAffiliateSaved} 
            />
          </div>
        );
      case 'affiliate-details':
        return selectedAffiliateId ? (
          <AffiliateDetails programId={program?.id} affiliateId={selectedAffiliateId} onBack={handleBackToList} />
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500">No affiliate selected. Please select an affiliate from the list.</p>
            <button
              onClick={() => setActiveTab('affiliates')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              View Affiliates
            </button>
          </div>
        );
      case 'signup-form':
        return <AffiliateSignupForm program={program} />;
      case 'payouts':
        return <PayoutsManagement programId={program?.id || ''} />;
      case 'reports':
        return <ReportsAnalytics programId={program?.id} />;
      case 'tracking-code':
        return <TrackingCodeGenerator programId={program?.id || null} programName={program?.program_name || 'Affiliate Program'} cookieDurationDays={program?.cookie_duration_days} />;
      case 'settings':
        return <AffiliateSettings programId={program?.id || null} />;
      default:
        return <ProgramOwnerDashboard program={program} />;
    }
  };
  
  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Affiliate Management</h1>
        
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px overflow-x-auto" role="tablist" aria-label="Affiliate management tabs">
              {(() => {
                const tabs = [
                  { key: 'overview', label: 'Dashboard' },
                  { key: 'programs', label: 'Programs' },
                  { key: 'plans', label: 'Plans' },
                  { key: 'tiers', label: 'Tiers' },
                  { key: 'approvals', label: 'Approvals' },
                  { key: 'attribution', label: 'Attribution' },
                  { key: 'webhooks', label: 'Webhooks' },
                  { key: 'commissions', label: 'Commissions' },
                  { key: 'creatives', label: 'Creatives' },
                  { key: 'integrations', label: 'Integrations' },
                  { key: 'affiliates', label: 'Affiliates' },
                  { key: 'signup-form', label: 'Signup Form' },
                  { key: 'payouts', label: 'Payouts' },
                  { key: 'reports', label: 'Reports' },
                  { key: 'tracking-code', label: 'Tracking Code' },
                  { key: 'settings', label: 'Settings' },
                ];
                const requiresProgram = (k: string) => ['plans','tiers','approvals','attribution','webhooks','commissions','affiliates','affiliate-details','payouts','reports','tracking-code','signup-form'].includes(k);
                return (
                  <div className="flex gap-6 min-w-max">
                    {tabs.map(t => {
                      const isActive = activeTab === t.key;
                      const needsProgram = requiresProgram(t.key);
                      const disabledStyle = !hasProgram && needsProgram ? 'text-gray-300 cursor-not-allowed' : '';
                      return (
                        <button
                          key={t.key}
                          id={t.key === 'webhooks' ? 'nav-webhooks' : undefined}
                          role="tab"
                          aria-selected={isActive}
                          aria-controls={`panel-${t.key}`}
                          onClick={() => setActiveTab(t.key)}
                          className={`${isActive ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} ${disabledStyle} whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm transition-colors`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </nav>
          </div>
        
        {renderContent()}
      </div>
    </div>
  );
};

export default AffiliateManagement;
